import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  activeUserId: string | null;
  impersonatedId: string | null;
  impersonatedName: string | null;
  impersonatedPhone: string | null;
  setImpersonation: (id: string | null, name: string | null, phone?: string | null) => void;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  activeUserId: null,
  impersonatedId: null,
  impersonatedName: null,
  impersonatedPhone: null,
  setImpersonation: () => {},
  loading: true,
  signOut: async () => { },
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [impersonatedId, setImpersonatedId] = useState<string | null>(null);
  const [impersonatedName, setImpersonatedName] = useState<string | null>(null);
  const [impersonatedPhone, setImpersonatedPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const activeUserId = impersonatedId || user?.id || null;

  const setImpersonation = (id: string | null, name: string | null, phone: string | null = null) => {
    setImpersonatedId(id);
    setImpersonatedName(name);
    setImpersonatedPhone(phone);
  };

  useEffect(() => {
    // Check active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes (login, logout, refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ 
      session, user, activeUserId, 
      impersonatedId, impersonatedName, impersonatedPhone, setImpersonation,
      loading, signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
